/**
 * Instrumentation. One thin wrapper over POST /api/events, and its failures
 * never reach a user.
 *
 * The allowlist below is duplicated server-side by design: the server drops
 * unknown names silently and counts them, and this copy exists so a typo is a
 * build-time-visible constant rather than a metric that is quietly always
 * zero. Four of these carry real decisions on their own — the daily-limit-hit
 * rate, the caption-path hit rate per platform, YouTube-without-captions, and
 * tool usage by tool — so a name that silently stops firing is a decision made
 * on missing data.
 */

import { peekSessionToken } from './session.ts';

/** Day-one names, from docs/USER_JOURNEY.md § Cross-cutting. */
export const EVENT_NAMES = [
  'limit_dialog_shown',
  'limit_dialog_cta',
  'limit_dialog_closed',
  'format_download',
  'tool_used',
  'cache_hit',
  'caption_hit',
  'error_state',
  'youtube_no_captions',
] as const;

export type EventName = (typeof EVENT_NAMES)[number];

const ALLOWED: ReadonlySet<string> = new Set(EVENT_NAMES);

interface QueuedEvent {
  name: string;
  props: Record<string, string | number | boolean>;
  at: number;
}

const queue: QueuedEvent[] = [];
/** Bounded so a runaway caller cannot grow the page's memory without limit. */
const MAX_QUEUE = 50;
const FLUSH_DELAY_MS = 3_000;
let timer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;

function bindListeners(): void {
  if (listenersBound) return;
  listenersBound = true;
  // pagehide, not unload: unload is not fired reliably and it disqualifies the
  // page from the back/forward cache. visibilitychange->hidden covers the tab
  // switch that never comes back.
  addEventListener('pagehide', () => flush(true), { capture: true });
  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
}

function schedule(): void {
  bindListeners();
  if (timer !== null) return;
  timer = setTimeout(() => {
    timer = null;
    void flush(false);
  }, FLUSH_DELAY_MS);
}

/**
 * Send whatever is queued, with the session's token if the page already has
 * one and without one if it does not.
 *
 * NEVER THROUGH THE API CLIENT, which mints a session when there is none, and
 * a mint is a Turnstile check. The page-unload path always knew that; the
 * normal one went through `request()`, so the first `tool_used` on a
 * client-side tool page put the "quick check that you're a person" card on a
 * page that never needed a session (live, 2026-10-06). The server takes
 * events with no session (`optional_session`); they are simply unattributed.
 *
 * `final` is the page-is-going-away path: keepalive, because the request must
 * outlive the document. `sendBeacon` is not usable — it cannot set
 * `X-Session-Token`, and the token is a header on this API by deliberate design.
 */
export function flush(final: boolean): Promise<void> {
  if (queue.length === 0) return Promise.resolve();
  const batch = queue.splice(0, queue.length);
  const token = peekSessionToken();
  try {
    return fetch('/api/events', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(token ? { 'X-Session-Token': token } : {}),
      },
      body: JSON.stringify({ events: batch }),
      keepalive: final,
      cache: 'no-store',
      credentials: 'omit',
    }).then(
      () => undefined,
      () => undefined,
    );
  } catch {
    return Promise.resolve(); // instrumentation never surfaces
  }
}

/**
 * Record an event. Returns nothing and throws nothing, ever.
 *
 * An unknown name is dropped with a console warning rather than sent: the
 * server would drop it anyway, and a warning at the call site is how a typo
 * gets found before launch instead of at the first metrics review.
 */
export function track(
  name: EventName | string,
  props: Record<string, string | number | boolean> = {},
): void {
  try {
    if (!ALLOWED.has(name)) {
      console.warn(`[events] "${name}" is not in the allowlist and was dropped`);
      return;
    }
    if (queue.length >= MAX_QUEUE) return;
    queue.push({ name, props, at: Math.floor(Date.now() / 1000) });
    schedule();
  } catch {
    /* instrumentation never surfaces */
  }
}
